import type {
  ProactiveAgentInstance,
  ProactiveAgents,
} from '@/features/settings/proactive-agent.query';

import type { ActionCard, Report, Routine } from '@littlebird/sdk';

/** Agents the home screen can show action cards for: those that can start, plus those that already have a chat */
export const proactiveAgentNames = (
  startableAgents: readonly string[],
  instances: readonly Pick<ProactiveAgentInstance, 'agent'>[]
): string[] => [
  ...new Set([
    ...startableAgents,
    ...instances.map((instance) => instance.agent),
  ]),
];

/**
 * Whether the agent has a job of its own, such as coding follow-up or email triage, so it is never
 * picked on the device's behalf
 */
export const hasJobOfItsOwn = (
  instance: Pick<ProactiveAgentInstance, 'kind'>
): boolean => instance.kind === 'coding' || instance.kind === 'email_triage';

/**
 * The agent whose action cards the home screen shows, or 'none'. This is the device's choice while
 * it is 'none' or a listed agent. Otherwise the device follows the user's default agent while the
 * default has no job of its own (see `hasJobOfItsOwn`) and has a chat that is not stopped; else it
 * shows the first such agent in `instances`, else the first of `names`.
 */
export const resolveSelectedProactiveAgent = (
  selected: string | null | undefined,
  names: readonly string[],
  instances: readonly Pick<
    ProactiveAgentInstance,
    'agent' | 'state' | 'kind'
  >[],
  defaultAgent: string | undefined
): string => {
  if (selected && (selected === 'none' || names.includes(selected))) {
    return selected;
  }
  const candidates = instances.filter(
    (instance) => instance.state !== 'stopped' && !hasJobOfItsOwn(instance)
  );
  return (
    (
      candidates.find((instance) => instance.agent === defaultAgent) ??
      candidates[0]
    )?.agent ??
    names[0] ??
    'none'
  );
};

/** Label for the agent whose cards the home screen shows */
export const proactiveAgentLabel = (selected: string): string =>
  selected === 'none' ? 'No proactive agent' : selected;

/** Removes items that belong to a hidden agent chat. `chatIdOf` returns an item's chat ID, if any. */
export const withoutHiddenAgentChats = <T>(
  items: T[],
  hiddenChatIds: ReadonlySet<string>,
  chatIdOf: (item: T) => string | null | undefined
): T[] =>
  hiddenChatIds.size === 0
    ? items
    : items.filter((item) => {
        const chatId = chatIdOf(item);
        return !chatId || !hiddenChatIds.has(chatId);
      });

/** Store lookups that trace a card to its agent: from the card's chat to its report, then to the report's routine */
export type CardSources = {
  readonly reportByChatId: (
    chatId: string
  ) => Pick<Report, 'reportTemplateId'> | undefined;
  readonly routineByReferenceId: (
    referenceId: number
  ) => Pick<Routine, 'createdInChatId'> | undefined;
};

type CardOwnerFields = Pick<ActionCard, 'chatId' | 'origin'> &
  Partial<Pick<ActionCard, 'reportTemplateId'>>;

/**
 * The chat a card belongs to: the chat that created the card's routine, else the card's own chat.
 * Null when it belongs to no chat, and undefined while its routine has not loaded.
 */
export const cardOwnerChatId = (
  card: CardOwnerFields,
  sources: CardSources
): string | null | undefined => {
  const routineId =
    card.reportTemplateId ??
    (card.chatId
      ? sources.reportByChatId(card.chatId)?.reportTemplateId
      : undefined);
  if (routineId === undefined) {
    return card.origin === 'routine' ? undefined : card.chatId;
  }
  return sources.routineByReferenceId(routineId)?.createdInChatId;
};

export const myProactiveAgentNames = (
  agents: Pick<ProactiveAgents, 'default_agent' | 'selected_agents'>
): readonly string[] => agents.selected_agents ?? [agents.default_agent];

/** Whether a card belongs to one of `hiddenChatIds` */
export const isFromHiddenAgentChat = (
  card: CardOwnerFields,
  hiddenChatIds: ReadonlySet<string>,
  sources: CardSources
): boolean => {
  if (hiddenChatIds.size === 0) return false;
  const ownerChatId = cardOwnerChatId(card, sources);
  return !!ownerChatId && hiddenChatIds.has(ownerChatId);
};

/**
 * Returns a filter for the action cards the home screen shows for `selectedAgent`. A card belongs
 * to the agent whose chat owns it, as `cardOwnerChatId` finds. A routine's card stays hidden until
 * its routine loads, so it never shows under the wrong agent. Cards that belong to no agent show
 * for every agent, unless `hidesCardsOfNoAgent` is set (the home switch Hide your own cards),
 * which shows only cards traced to `selectedAgent`
 */
export const proactiveAgentCardFilter = (
  selectedAgent: string | readonly string[],
  instances: readonly Pick<
    ProactiveAgentInstance,
    'chat_id' | 'agent' | 'kind'
  >[],
  sources: CardSources,
  hidesCardsOfNoAgent = false
) => {
  const instanceByChatId = new Map(
    instances.map((instance) => [instance.chat_id, instance])
  );
  const selectedAgents = new Set(
    typeof selectedAgent === 'string' ? [selectedAgent] : selectedAgent
  );
  const accepts = (ownerChatId: string | null): boolean => {
    const owner = ownerChatId ? instanceByChatId.get(ownerChatId) : undefined;
    return (
      (owner !== undefined && selectedAgents.has(owner.agent)) ||
      (owner === undefined && !hidesCardsOfNoAgent)
    );
  };
  return (card: CardOwnerFields): boolean => {
    const ownerChatId = cardOwnerChatId(card, sources);
    if (ownerChatId === undefined) {
      return !hidesCardsOfNoAgent && card.origin !== 'routine';
    }
    return accepts(ownerChatId);
  };
};
